import React from "react";
import { Box, Grid } from "@mui/material";
import {
  EvStation, ElectricCar, BatteryChargingFull,
  CurrencyRupeeOutlined, WarningAmberOutlined, TimerOutlined,
} from "@mui/icons-material";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import DemoShell from "./DemoShell";
import { MetricCard, ChartCard, DarkTooltip } from "./demoComponents";
import { demo } from "./demoStyles";

// ── Mock data ────────────────────────────────────────────────────────────────

// Hourly sessions + energy dispensed across the day
const hourlyData = Array.from({ length: 24 }, (_, h) => {
  const sessionPeak = h >= 8 && h <= 22
    ? Math.round(4 + Math.sin(((h - 8) / 14) * Math.PI) * 28 + (Math.random() - 0.5) * 6)
    : Math.floor(Math.random() * 3);
  const energy = Math.round(sessionPeak * (7 + Math.random() * 4));
  return { hour: `${h}:00`, sessions: Math.max(0, sessionPeak), kWh: Math.max(0, energy) };
});

// Weekly sessions split by charger type
const weeklyData = [
  { day: "Mon", AC: 124, DC: 58  },
  { day: "Tue", AC: 138, DC: 64  },
  { day: "Wed", AC: 119, DC: 52  },
  { day: "Thu", AC: 146, DC: 72  },
  { day: "Fri", AC: 168, DC: 84  },
  { day: "Sat", AC: 192, DC: 96  },
  { day: "Sun", AC: 178, DC: 88  },
];

// Charger status distribution
const statusData = [
  { name: "Available",  value: 142, color: demo.green  },
  { name: "Charging",   value: 84,  color: demo.accent  },
  { name: "Offline",    value: 16,  color: demo.textDim },
  { name: "Fault",      value: 6,   color: "#f87171"   },
];

// Energy by charger type for revenue breakdown
const revenueByType = [
  { type: "3.3 kW AC",  sessions: 312, revenue: 28080 },
  { type: "7.4 kW AC",  sessions: 284, revenue: 51120 },
  { type: "22 kW AC",   sessions: 196, revenue: 58800 },
  { type: "50 kW DC",   sessions: 148, revenue: 88800 },
  { type: "120 kW DC",  sessions: 84,  revenue: 100800 },
];

// Recent charging sessions
const recentSessions = [
  { id: "S-48921", vehicle: "MH 12 AB 4821", charger: "CHG-DL-042 (50kW DC)", duration: "38 min", energy: "31.6 kWh", cost: "₹948",  status: "Completed" },
  { id: "S-48920", vehicle: "KA 09 MN 2210", charger: "CHG-MH-018 (22kW AC)", duration: "62 min", energy: "22.8 kWh", cost: "₹684",  status: "Completed" },
  { id: "S-48919", vehicle: "DL 04 CF 7731", charger: "CHG-DL-011 (7.4kW AC)", duration: "—",     energy: "8.4 kWh",  cost: "₹252",  status: "Active"    },
  { id: "S-48918", vehicle: "GJ 05 WX 3308", charger: "CHG-GJ-029 (120kW DC)", duration: "—",     energy: "42.1 kWh", cost: "₹1,263", status: "Active"   },
  { id: "S-48917", vehicle: "UP 80 KL 9912", charger: "CHG-UP-007 (3.3kW AC)", duration: "94 min", energy: "5.2 kWh", cost: "₹156",  status: "Completed" },
];

const GRADIENT = "linear-gradient(135deg, #34d399, #22d3ee)";
const statusColor = { Completed: demo.green, Active: demo.accent, Fault: "#f87171" };

export default function EVChargersPage() {
  return (
    <DemoShell title="EV Chargers" icon={EvStation} gradient={GRADIENT}>

      {/* ── KPI row ── */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[
          { icon: EvStation,              label: "Total Chargers",        value: "248",     sub: "Across 64 stations",          gradient: GRADIENT },
          { icon: ElectricCar,            label: "Active Sessions",       value: "84",      trend: "+12 vs 1 hr ago", trendUp: true,  gradient: GRADIENT },
          { icon: BatteryChargingFull,    label: "Energy Dispensed Today",value: "1,842",   unit: "kWh", trend: "+6% vs yesterday", trendUp: true, gradient: GRADIENT },
          { icon: CurrencyRupeeOutlined,  label: "Revenue Today",         value: "₹55,260", trend: "+8% vs yesterday", trendUp: true,  gradient: GRADIENT },
          { icon: TimerOutlined,          label: "Avg Session Duration",  value: "48",      unit: "min", sub: "Across all charger types", gradient: "linear-gradient(135deg, #a78bfa, #6366f1)" },
          { icon: WarningAmberOutlined,   label: "Faults / Offline",      value: "22",      sub: "6 faults · 16 offline",       gradient: "linear-gradient(135deg, #fb923c, #f87171)" },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <MetricCard {...m} />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts row 1 ── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>

        {/* 24-hour sessions + energy area */}
        <Grid item xs={12} lg={8}>
          <ChartCard title="24-Hour Activity" subtitle="Concurrent sessions & energy dispensed · Today">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={hourlyData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#34d399" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0}    />
                  </linearGradient>
                  <linearGradient id="energyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#22d3ee" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="hour" tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} interval={3} />
                <YAxis yAxisId="left"  tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={({ active, payload, label }) =>
                  active && payload?.length
                    ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "10px", px: 1.8, py: 1.2 }}>
                        <Box sx={{ color: demo.textDim, fontSize: 11.5, mb: 0.8, fontWeight: 600 }}>{label}</Box>
                        {payload.map((p, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: i < payload.length - 1 ? 0.5 : 0 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
                            <Box sx={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>
                              {p.value} {p.dataKey === "sessions" ? "sessions" : "kWh"}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    : null
                } />
                <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12 }}>{v === "sessions" ? "Sessions" : "Energy (kWh)"}</span>} />
                <Area yAxisId="left"  type="monotone" dataKey="sessions" stroke="#34d399" strokeWidth={2.5}
                  fill="url(#sessGrad)"   dot={false} activeDot={{ r: 5, fill: "#34d399" }} />
                <Area yAxisId="right" type="monotone" dataKey="kWh"      stroke="#22d3ee" strokeWidth={2}
                  fill="url(#energyGrad)" dot={false} activeDot={{ r: 5, fill: "#22d3ee" }} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Charger status donut */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard title="Charger Status" subtitle="Live fleet availability">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={65} outerRadius={95}
                  paddingAngle={3} dataKey="value">
                  {statusData.map((e, i) => <Cell key={i} fill={e.color} stroke="transparent" />)}
                </Pie>
                <Tooltip content={({ active, payload }) =>
                  active && payload?.length
                    ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "8px", px: 1.6, py: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: payload[0].payload.color }} />
                          <span style={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>
                            {payload[0].payload.name}: {payload[0].value}
                          </span>
                        </Box>
                      </Box>
                    : null
                } />
                <Legend formatter={(v, e) => (
                  <span style={{ color: demo.textDim, fontSize: 12 }}>
                    {e.payload.name} <span style={{ color: e.payload.color, fontWeight: 700 }}>({e.payload.value})</span>
                  </span>
                )} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* ── Charts row 2 ── */}
      <Grid container spacing={2.5}>

        {/* Weekly sessions by charger type */}
        <Grid item xs={12} lg={5}>
          <ChartCard title="Weekly Sessions by Charger Type" subtitle="AC vs DC · Last 7 days">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={weeklyData} margin={{ top: 4, right: 4, bottom: 0, left: -15 }} barGap={4}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: demo.textFaint, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<DarkTooltip unit=" sessions" />} />
                <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12 }}>{v === "AC" ? "AC Chargers" : "DC Fast Chargers"}</span>} />
                <Bar dataKey="AC" fill="#34d399" radius={[4,4,0,0]} maxBarSize={26} />
                <Bar dataKey="DC" fill="#22d3ee" radius={[4,4,0,0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Revenue by charger type + recent sessions */}
        <Grid item xs={12} lg={7}>
          <ChartCard title="Recent Charging Sessions" subtitle="Latest 5 transactions">
            <Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "70px 1fr 110px 55px 55px 65px 75px",
                gap: "0 10px", pb: 1, mb: 0.5, borderBottom: `1px solid ${demo.border}` }}>
                {["ID", "Vehicle", "Charger", "Time", "Energy", "Cost", "Status"].map(h => (
                  <Box key={h} sx={{ color: demo.textFaint, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</Box>
                ))}
              </Box>
              {recentSessions.map((s, i) => (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "70px 1fr 110px 55px 55px 65px 75px",
                  gap: "0 10px", py: 1.2, borderBottom: `1px solid ${demo.border}`,
                  "&:last-child": { borderBottom: "none" }, alignItems: "center" }}>
                  <Box sx={{ color: demo.accent, fontSize: 12, fontWeight: 700 }}>{s.id}</Box>
                  <Box sx={{ color: demo.text, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.vehicle}</Box>
                  <Box sx={{ color: demo.textDim, fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.charger}</Box>
                  <Box sx={{ color: demo.textDim, fontSize: 12 }}>{s.duration}</Box>
                  <Box sx={{ color: demo.text, fontSize: 12.5, fontWeight: 600 }}>{s.energy}</Box>
                  <Box sx={{ color: demo.text, fontSize: 12.5, fontWeight: 700 }}>{s.cost}</Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: statusColor[s.status],
                      boxShadow: s.status === "Active" ? `0 0 7px ${demo.accent}` : "none" }} />
                    <Box sx={{ color: statusColor[s.status], fontSize: 11.5, fontWeight: 600 }}>{s.status}</Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Revenue breakdown mini table */}
            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${demo.border}` }}>
              <Box sx={{ color: demo.textDim, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.2 }}>
                Revenue by Charger Type · Today
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "100px 1fr 1fr 80px", gap: "0 12px",
                pb: 0.8, mb: 0.5, borderBottom: `1px solid ${demo.border}` }}>
                {["Type", "Sessions", "Revenue", "Avg/Session"].map(h => (
                  <Box key={h} sx={{ color: demo.textFaint, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase" }}>{h}</Box>
                ))}
              </Box>
              {revenueByType.map((r, i) => (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "100px 1fr 1fr 80px", gap: "0 12px",
                  py: 0.9, borderBottom: `1px solid ${demo.border}`, "&:last-child": { borderBottom: "none" } }}>
                  <Box sx={{ color: demo.text, fontSize: 12.5, fontWeight: 600 }}>{r.type}</Box>
                  <Box sx={{ color: demo.textDim, fontSize: 12.5 }}>{r.sessions.toLocaleString()}</Box>
                  <Box sx={{ color: demo.green, fontSize: 12.5, fontWeight: 700 }}>₹{(r.revenue/100).toFixed(0) * 100 >= 100000 ? `${(r.revenue/100000).toFixed(2)}L` : r.revenue.toLocaleString()}</Box>
                  <Box sx={{ color: demo.textDim, fontSize: 12 }}>₹{Math.round(r.revenue / r.sessions)}</Box>
                </Box>
              ))}
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </DemoShell>
  );
}
